import React from "react";
import { cn } from "@/lib/utils";

/**
 * Skeleton row component matching MessageRow design
 */
const SkeletonRow = ({ index }: { index: number }) => (
  <div
    className={cn(
      // Match MessageRow styling
      "relative",
      "bg-white/80 dark:bg-slate-800/60",
      "border border-slate-200/60 dark:border-slate-700/50",
      "border-l-4 border-l-slate-300/50 dark:border-l-slate-600/50",
      "rounded-lg",
      "shadow-[0_2px_12px_rgba(15,23,42,0.04)]",
      "dark:shadow-[0_2px_12px_rgba(0,0,0,0.2)]",
      "mb-3 px-5 py-4",
      // Animation
      "animate-in fade-in-0",
      "fill-mode-both"
    )}
    style={{
      animationDelay: `${index * 50}ms`,
      animationDuration: "400ms",
    }}
  >
    <div className="flex items-center justify-between gap-4">
      {/* Left section - Title and metadata */}
      <div className="flex-1 min-w-0">
        {/* Badge skeleton */}
        <div className="mb-2">
          <div
            className={cn(
              "h-5 w-16 rounded-full",
              "skeleton-shimmer"
            )}
          />
        </div>

        {/* Title skeleton */}
        <div
          className={cn(
            "h-5 rounded",
            "skeleton-shimmer",
            "w-[70%]"
          )}
        />

        {/* Location skeleton (optional) */}
        <div
          className={cn(
            "h-3 rounded mt-2",
            "skeleton-shimmer",
            "w-[40%]"
          )}
        />
      </div>

      {/* Center - Timestamp (hidden on mobile) */}
      <div className="hidden sm:flex flex-col items-end shrink-0">
        <div
          className={cn(
            "h-3 w-16 rounded mb-1",
            "skeleton-shimmer"
          )}
        />
        <div
          className={cn(
            "h-4 w-24 rounded",
            "skeleton-shimmer"
          )}
        />
      </div>

      {/* Right - Action buttons */}
      <div className="flex items-center gap-2 shrink-0">
        <div
          className={cn(
            "h-8 w-16 lg:w-20 rounded-md",
            "skeleton-shimmer"
          )}
        />
        <div
          className={cn(
            "h-8 w-16 lg:w-24 rounded-md",
            "skeleton-shimmer"
          )}
        />
      </div>
    </div>

    {/* Mobile timestamp */}
    <div className="sm:hidden mt-2 pt-2 border-t border-slate-100 dark:border-slate-700/50">
      <div
        className={cn(
          "h-3 w-32 rounded",
          "skeleton-shimmer"
        )}
      />
    </div>
  </div>
);

/**
 * Skeleton card component matching MessageCard design (mobile)
 */
const SkeletonCard = ({ index }: { index: number }) => (
  <div
    className={cn(
      // Match MessageCard styling
      "relative overflow-hidden",
      "bg-white/90 dark:bg-slate-800/70",
      "border border-slate-200/60 dark:border-slate-700/50",
      "border-l-4 border-l-slate-300/50 dark:border-l-slate-600/50",
      "rounded-xl",
      "shadow-[0_4px_16px_rgba(15,23,42,0.05)]",
      "dark:shadow-[0_4px_16px_rgba(0,0,0,0.2)]",
      "mb-4 p-4",
      // Animation
      "animate-in fade-in-0",
      "fill-mode-both"
    )}
    style={{
      animationDelay: `${index * 50}ms`,
      animationDuration: "400ms",
    }}
  >
    {/* Header */}
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="flex-1 min-w-0">
        {/* Badge */}
        <div className="mb-2">
          <div
            className={cn(
              "h-5 w-20 rounded-full",
              "skeleton-shimmer"
            )}
          />
        </div>

        {/* Title */}
        <div
          className={cn(
            "h-5 rounded",
            "skeleton-shimmer",
            "w-[85%]"
          )}
        />
        <div
          className={cn(
            "h-5 rounded mt-1",
            "skeleton-shimmer",
            "w-[60%]"
          )}
        />
      </div>

      {/* Indicator dot */}
      <div
        className={cn(
          "w-2.5 h-2.5 rounded-full shrink-0 mt-1",
          "skeleton-shimmer"
        )}
      />
    </div>

    {/* Metadata row */}
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-4">
      <div
        className={cn(
          "h-4 w-24 rounded",
          "skeleton-shimmer"
        )}
      />
      <div
        className={cn(
          "h-4 w-32 rounded",
          "skeleton-shimmer"
        )}
      />
    </div>

    {/* Action buttons */}
    <div className="flex gap-2">
      <div
        className={cn(
          "flex-1 h-10 rounded-md",
          "skeleton-shimmer"
        )}
      />
      <div
        className={cn(
          "flex-1 h-10 rounded-md",
          "skeleton-shimmer"
        )}
      />
    </div>
  </div>
);

interface MessagesTableSkeletonProps {
  /**
   * Number of skeleton rows to display
   */
  rows?: number;
  /**
   * Whether to show mobile card layout
   */
  isMobile?: boolean;
}

/**
 * Skeleton loader for the messages table/list
 * Displays animated placeholder rows while data is loading
 */
export function MessagesTableSkeleton({
  rows = 5,
  isMobile = false,
}: MessagesTableSkeletonProps) {
  return (
    <div className="space-y-0">
      {/* Add shimmer CSS via style tag */}
      <style>{`
        .skeleton-shimmer {
          background: linear-gradient(
            90deg,
            hsl(var(--muted)) 0px,
            hsl(var(--background)) 50px,
            hsl(var(--muted)) 100px
          );
          background-size: 200px 100%;
          animation: shimmer 1.5s infinite linear;
        }

        @keyframes shimmer {
          0% {
            background-position: -200px 0;
          }
          100% {
            background-position: 200px 0;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .skeleton-shimmer {
            animation: pulse 2s ease-in-out infinite;
          }

          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        }
      `}</style>

      {Array.from({ length: rows }).map((_, index) =>
        isMobile ? (
          <SkeletonCard key={index} index={index} />
        ) : (
          <SkeletonRow key={index} index={index} />
        )
      )}
    </div>
  );
}

export default MessagesTableSkeleton;
